import {
    RuleActionType,
    RuleConditionType,
    TransactionRuleAction,
    TransactionRuleCondition,
    TransferRuleDirection,
} from "@common/types/transactionRule";
import { Counterparty } from "@common/types/counterparty";
import { NormalizedDescriptionInfo } from "@common/types/normalizedDescription";
import { isRecord } from "../../utils/historyDetailsHelpers";

interface RuleSnapshotData {
    conditions: TransactionRuleCondition[];
    actions: TransactionRuleAction[];
    counterparties: Counterparty[];
    normalizedDescriptions: NormalizedDescriptionInfo[];
}

const CONDITION_PREFIX = "condition:";
const ACTION_PREFIX = "action:";

interface IdNamePair {
    id: unknown;
    name: string;
}

function isIdNamePair(v: unknown): v is IdNamePair {
    return (
        isRecord(v) &&
        "id" in v &&
        "name" in v &&
        typeof (v as { name: unknown }).name === "string"
    );
}

function normalizeValue(raw: unknown): {
    value: unknown;
    pairs: IdNamePair[];
} {
    if (Array.isArray(raw)) {
        const pairs: IdNamePair[] = [];
        const value = raw.map((item) => {
            if (isIdNamePair(item)) {
                pairs.push(item);
                return item.id;
            }
            return item;
        });
        return { value, pairs };
    }
    if (isIdNamePair(raw)) {
        return { value: raw.id, pairs: [raw] };
    }
    return { value: raw, pairs: [] };
}

function buildCounterparty(pair: IdNamePair): Counterparty {
    return {
        id: String(pair.id),
        name: pair.name,
        companyName: null,
        workspaceId: null,
        updatedByUserId: null,
        source: null,
        sourceId: null,
        email: null,
        cc: null,
        bcc: null,
        phoneNumber: null,
        website: null,
        title: null,
        firstName: null,
        middleName: null,
        lastName: null,
        suffix: null,
        street: null,
        street2: null,
        city: null,
        postalCode: null,
        state: null,
        country: null,
        shouldBeNormalized: false,
        directionOverride: null,
        isEligible1099: null,
        federalTaxClassification: null,
        note: null,
        disabledAt: null,
    };
}

function addMissingCounterparties(
    counterparties: Map<string, Counterparty>,
    pairs: IdNamePair[],
): void {
    for (const pair of pairs) {
        const id = String(pair.id);
        if (!counterparties.has(id)) {
            counterparties.set(id, buildCounterparty(pair));
        }
    }
}

function toTransferDirection(v: unknown): TransferRuleDirection | null {
    if (v === TransferRuleDirection.FROM || v === TransferRuleDirection.TO) {
        return v;
    }
    return null;
}

function buildNormalizedDescription(
    pair: IdNamePair,
): NormalizedDescriptionInfo {
    return {
        id: String(pair.id),
        normalizedDescription: pair.name,
    };
}

function addMissingNormalizedDescriptions(
    normalizedDescriptions: Map<string, NormalizedDescriptionInfo>,
    pairs: IdNamePair[],
): void {
    for (const pair of pairs) {
        const id = String(pair.id);
        if (!normalizedDescriptions.has(id)) {
            normalizedDescriptions.set(id, buildNormalizedDescription(pair));
        }
    }
}

function readPerFieldEntries(snapshot: Record<string, unknown>): {
    conditions: TransactionRuleCondition[];
    actions: TransactionRuleAction[];
    counterparties: Map<string, Counterparty>;
    normalizedDescriptions: Map<string, NormalizedDescriptionInfo>;
} {
    const conditions: TransactionRuleCondition[] = [];
    const actions: TransactionRuleAction[] = [];
    const counterparties = new Map<string, Counterparty>();
    const normalizedDescriptions = new Map<string, NormalizedDescriptionInfo>();

    for (const [key, raw] of Object.entries(snapshot)) {
        if (!isRecord(raw)) {
            continue;
        }

        if (key.startsWith(CONDITION_PREFIX)) {
            const conditionType = raw.conditionType as RuleConditionType;
            const { value, pairs } = normalizeValue(raw.value);
            conditions.push({
                id: key,
                conditionType,
                value,
                transferDirection: toTransferDirection(raw.transferDirection),
            } as TransactionRuleCondition);

            if (conditionType === RuleConditionType.COUNTERPARTY) {
                addMissingCounterparties(counterparties, pairs);
            }
            if (conditionType === RuleConditionType.SIMILAR_TO) {
                addMissingNormalizedDescriptions(normalizedDescriptions, pairs);
            }
            continue;
        }

        if (key.startsWith(ACTION_PREFIX)) {
            const actionType = raw.actionType as RuleActionType;
            const { value, pairs } = normalizeValue(raw.value);
            actions.push({
                id: key,
                actionType,
                value,
                transferDirection: toTransferDirection(raw.transferDirection),
            } as TransactionRuleAction);

            if (
                actionType === RuleActionType.COUNTERPARTY ||
                actionType === RuleActionType.CUSTOMER
            ) {
                addMissingCounterparties(counterparties, pairs);
            }
        }
    }

    return { conditions, actions, counterparties, normalizedDescriptions };
}

function readLegacyConditions(raw: unknown): TransactionRuleCondition[] {
    if (!Array.isArray(raw)) {
        return [];
    }
    return raw
        .filter(
            (item): item is Record<string, unknown> =>
                isRecord(item) && "conditionType" in item,
        )
        .map((item, index) => {
            const { value } = normalizeValue(item.value);
            return {
                id: String(item.id ?? `legacy-cond-${index}`),
                conditionType: item.conditionType,
                value,
                transferDirection: toTransferDirection(item.transferDirection),
            } as TransactionRuleCondition;
        });
}

function readLegacyActions(raw: unknown): TransactionRuleAction[] {
    if (!Array.isArray(raw)) {
        return [];
    }
    return raw
        .filter(
            (item): item is Record<string, unknown> =>
                isRecord(item) && "actionType" in item,
        )
        .map((item, index) => {
            const { value } = normalizeValue(item.value);
            return {
                id: String(item.id ?? `legacy-act-${index}`),
                actionType: item.actionType,
                value,
                transferDirection: toTransferDirection(item.transferDirection),
            } as TransactionRuleAction;
        });
}

export function extractRuleDataFromSnapshot(
    snapshot: Record<string, unknown> | undefined | null,
    resource?: Record<string, unknown>,
): RuleSnapshotData {
    const empty: RuleSnapshotData = {
        conditions: [],
        actions: [],
        counterparties: [],
        normalizedDescriptions: [],
    };

    const perField = snapshot
        ? readPerFieldEntries(snapshot)
        : {
              conditions: [],
              actions: [],
              counterparties: new Map<string, Counterparty>(),
              normalizedDescriptions: new Map<
                  string,
                  NormalizedDescriptionInfo
              >(),
          };

    if (perField.conditions.length > 0 || perField.actions.length > 0) {
        return {
            conditions: perField.conditions,
            actions: perField.actions,
            counterparties: Array.from(perField.counterparties.values()),
            normalizedDescriptions: Array.from(
                perField.normalizedDescriptions.values(),
            ),
        };
    }

    const legacySource = snapshot ?? {};
    const fromSnapshotConditions = readLegacyConditions(
        legacySource.conditions,
    );
    const fromSnapshotActions = readLegacyActions(legacySource.actions);

    const conditions = fromSnapshotConditions.length
        ? fromSnapshotConditions
        : readLegacyConditions(resource?.conditions);
    const actions = fromSnapshotActions.length
        ? fromSnapshotActions
        : readLegacyActions(resource?.actions);

    if (conditions.length === 0 && actions.length === 0) {
        return empty;
    }

    return {
        conditions,
        actions,
        counterparties: [],
        normalizedDescriptions: [],
    };
}

export function mergeSnapshotData(
    ...parts: RuleSnapshotData[]
): Pick<RuleSnapshotData, "counterparties" | "normalizedDescriptions"> {
    const cps = new Map<string, Counterparty>();
    const nds = new Map<string, NormalizedDescriptionInfo>();

    for (const part of parts) {
        for (const cp of part.counterparties) {
            if (!cps.has(cp.id)) {
                cps.set(cp.id, cp);
            }
        }
        for (const nd of part.normalizedDescriptions) {
            if (!nds.has(nd.id)) {
                nds.set(nd.id, nd);
            }
        }
    }

    return {
        counterparties: Array.from(cps.values()),
        normalizedDescriptions: Array.from(nds.values()),
    };
}
